import {
  MutationErrorResponse,
  MutationErrorResponseMessageExtensions,
} from '../../graphql/Interface';

/**
 * Extracts the name of the field and its index (if any) from the extensions
 * defined at a mutation error response message. This supports the following
 * scenarios:
 *  1. Field which isn't an array: "{ attribute: "field" }"
 *  2. Field which is an array: "{ attribute: "fields[0]" }"
 *
 * @warn this doesn't support the scenario where object keys are returned, for example:
 *  { attribute: "field.child.subchild[1]" }
 *
 * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/application/concerns/execution_error_responder.rb
 */
export function parseMutationErrorExtensions(
  extensions: MutationErrorResponseMessageExtensions
): [string, number] {
  const { attribute } = extensions;

  const matches = attribute.match(/([a-zA-Z]+)\[?([0-9]+)?\]?/);
  if (matches) {
    const [, field, index] = matches;
    return [field, index ? +index : -1];
  }

  return ['', -1];
}

export type MutationErrorMessage = string;
export type MutationErrorFields = Record<string, number[]>;

/**
 * Parses a mutation error, building an object of all the fields (and their indexes,
 * if said fields are supposed to be sent in an array) and the error message
 * returned for those errors, in order to have a convenient way to display
 * error messages to users for specific form fields.
 *
 * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/application/concerns/execution_error_responder.rb
 */
export function parseMutationError<T>(
  e: MutationErrorResponse<T>
): string | [MutationErrorMessage, MutationErrorFields] {
  if (typeof e === 'string') {
    return e;
  }

  let errorMessage = '';
  const errorFieldsMap = new Map<string, number[]>();

  e.forEach(({ extensions, message }) => {
    errorMessage = message;

    if (!extensions) {
      return;
    }

    // file deepcode ignore MissingArgument: Snyk is grabbing this as an issue, but it's not
    const [field, index] = parseMutationErrorExtensions(extensions);
    if (field === '') {
      return;
    }

    const prevIndexes = errorFieldsMap.get(field) || [];
    const indexes = index === -1 ? prevIndexes : [...prevIndexes, index];
    errorFieldsMap.set(field, indexes);
  });

  return [errorMessage, Object.fromEntries(errorFieldsMap.entries())];
}
